import { useEffect, useState } from 'react';
import { Outlet, Link as RouterLink, useLocation } from 'react-router-dom';
import { useIsAuthenticated } from '@azure/msal-react';
import { Box, Flex, HStack, Image, Text } from '@chakra-ui/react';

/** Nav bar height — the brand lockup needs more room than the old wordmark. */
export const NAV_HEIGHT = '104px';

const NAV_LINKS = [
  { to: '/', label: 'Home' },
  { to: '/services', label: 'Services' },
  { to: '/about', label: 'About' },
  { to: '/pricing', label: 'Pricing' },
  { to: '/contact', label: 'Contact Me' },
];

export function PublicLayout() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const isAuthenticated = useIsAuthenticated();
  // Admin access entry, right of Contact Me.
  const loginLabel = isAuthenticated ? 'Portal' : 'Login';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <Box>
      <Flex
        as="nav"
        position="fixed"
        top="0"
        left="0"
        right="0"
        zIndex="300"
        h={NAV_HEIGHT}
        align="center"
        justify="space-between"
        px={{ base: '1.5rem', md: '3.5rem' }}
        bg={scrolled ? 'rgba(255,255,255,0.96)' : 'rgba(255,255,255,0.78)'}
        backdropFilter="blur(16px)"
        borderBottom="1px solid rgba(200,196,190,0.4)"
        transition="background 0.3s"
      >
        <Flex asChild align="center" gap="0.9rem" textDecoration="none">
          <RouterLink to="/">
            <Image
              src="/assets/augusta-monogram-badge.png"
              alt="Augusta Executive Solutions monogram"
              w={{ base: '50px', md: '68px' }}
              h={{ base: '50px', md: '68px' }}
              display="block"
              flexShrink="0"
            />
            <Flex direction="column">
              <Text
                fontFamily="heading"
                fontSize={{ base: '1.12rem', md: '1.34rem' }}
                fontWeight="400"
                letterSpacing="0.01em"
                color="brand.ink"
                whiteSpace="nowrap"
              >
                Augusta{' '}
                <Box as="em" fontStyle="italic" color="brand.mid">
                  Executive Solutions
                </Box>
              </Text>
              <Box h="2px" bg="brand.lime" mt="0.22rem" mb="0.16rem" />
              <Text
                fontFamily="heading"
                fontStyle="italic"
                fontSize={{ base: '0.9rem', md: '1.04rem' }}
                color="ink.muted"
                whiteSpace="nowrap"
              >
                Calm in the chaos.
              </Text>
            </Flex>
          </RouterLink>
        </Flex>

        <HStack as="ul" listStyleType="none" display={{ base: 'none', md: 'flex' }} gap="0">
          {NAV_LINKS.map((link) => {
            const active = location.pathname === link.to;
            return (
              <Box as="li" key={link.to}>
                <Box
                  asChild
                  display="block"
                  px="1.35rem"
                  fontSize="0.71rem"
                  fontWeight="500"
                  letterSpacing="0.14em"
                  textTransform="uppercase"
                  color={active ? 'brand.terracotta' : 'ink.body'}
                  textDecoration="none"
                  h={NAV_HEIGHT}
                  lineHeight={NAV_HEIGHT}
                  borderBottom="2px solid"
                  borderBottomColor={active ? 'brand.mid' : 'transparent'}
                  transition="color 0.4s ease, border-color 0.6s ease 0.35s"
                  _hover={{
                    color: 'brand.terracotta',
                    borderBottomColor: 'brand.mid',
                    animation: 'greenFlash 1.4s ease-out',
                  }}
                >
                  <RouterLink to={link.to}>{link.label}</RouterLink>
                </Box>
              </Box>
            );
          })}
          <Box as="li">
            <Box
              asChild
              display="block"
              px="1.35rem"
              fontSize="0.71rem"
              fontWeight="500"
              letterSpacing="0.14em"
              textTransform="uppercase"
              color="brand.mid"
              textDecoration="none"
              h={NAV_HEIGHT}
              lineHeight={NAV_HEIGHT}
              borderBottom="2px solid transparent"
              transition="color 0.4s ease, border-color 0.6s ease 0.35s"
              _hover={{
                color: 'brand.terracotta',
                borderBottomColor: 'brand.mid',
                animation: 'greenFlash 1.4s ease-out',
              }}
            >
              <RouterLink to="/portal">{loginLabel}</RouterLink>
            </Box>
          </Box>
        </HStack>

        <Flex
          as="button"
          aria-label="Menu"
          display={{ base: 'flex', md: 'none' }}
          direction="column"
          gap="5px"
          cursor="pointer"
          bg="none"
          border="none"
          p="4px"
          onClick={() => setMenuOpen((open) => !open)}
        >
          <Box w="22px" h="1.5px" bg="brand.ink" />
          <Box w="22px" h="1.5px" bg="brand.ink" />
          <Box w="22px" h="1.5px" bg="brand.ink" />
        </Flex>
      </Flex>

      {menuOpen && (
        <Flex
          position="fixed"
          top={NAV_HEIGHT}
          left="0"
          right="0"
          zIndex="299"
          bg="paper.white"
          borderBottom="1px solid"
          borderBottomColor="paper.border"
          direction="column"
          px="2rem"
          py="1.5rem"
        >
          {NAV_LINKS.map((link, i) => (
            <Box
              key={link.to}
              asChild
              fontSize="0.78rem"
              fontWeight="500"
              letterSpacing="0.13em"
              textTransform="uppercase"
              color={
                i === NAV_LINKS.length - 1
                  ? 'brand.mid'
                  : location.pathname === link.to
                    ? 'brand.ink'
                    : 'ink.body'
              }
              textDecoration="none"
              py="0.85rem"
              borderBottom={i === NAV_LINKS.length - 1 ? 'none' : '1px solid'}
              borderBottomColor="paper.border"
            >
              <RouterLink to={link.to}>
                {link.label}
                {i === NAV_LINKS.length - 1 ? ' →' : ''}
              </RouterLink>
            </Box>
          ))}
          <Box
            asChild
            fontSize="0.78rem"
            fontWeight="500"
            letterSpacing="0.13em"
            textTransform="uppercase"
            color="brand.mid"
            textDecoration="none"
            py="0.85rem"
            mt="0.5rem"
          >
            <RouterLink to="/portal">{loginLabel}</RouterLink>
          </Box>
        </Flex>
      )}

      <Outlet />
    </Box>
  );
}

export function SiteFooter({
  copyright,
  variant = 'linen',
}: {
  copyright: string;
  variant?: 'linen' | 'glass';
}) {
  const glass = variant === 'glass';
  return (
    <Flex
      as="footer"
      position="relative"
      zIndex="2"
      bg={glass ? 'rgba(255,255,255,0.32)' : 'paper.linen'}
      backdropFilter={glass ? 'blur(2px)' : undefined}
      borderTop="1px solid"
      borderTopColor="paper.border"
      px={{ base: '1.8rem', md: '3.5rem' }}
      py="2.8rem"
      align={{ base: 'flex-start', md: 'center' }}
      justify="flex-end"
      direction={{ base: 'column', md: 'row' }}
      gap="1rem"
      wrap="wrap"
    >
      <Text fontSize="0.7rem" letterSpacing="0.06em" color="ink.muted" ml="auto">
        {copyright}
      </Text>
    </Flex>
  );
}
